import { useManager } from 'dreamstate';
import { useCallback, useState, FocusEvent, LegacyRef, ReactElement } from 'react';

import { IconButton, Menu, MenuButton, MenuItem, MenuList, rem } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { useControlledPopover } from '../../../hooks/dom/useControlledPopover';
import { PollManager, IPollContext } from '../../../store/firebase/interaction/poll';
import { T_DOWNLOAD, T_SETTINGS } from '../../../translations';
import { createDomName } from '../../../utils/dom/identifying';
import { BokehTooltip } from '../../bokeh';
import { LiveErrorBoundary } from '../../error/boundary';
import { DownloadModal } from '../shared/DownloadModal';
import { InteractionToolsSubmenu } from '../shared/InteractionToolsSubmenu';

interface IPollSettingsButtonProps extends IBaseDomElementProps {
  withInteractionSubmenu?: boolean;
  buttonIcon: ReactElement;
  pollContext?: IPollContext;
}

export function PollSettingsButton({
  id = createDomName('poll-settings-button'),
  className = createDomName('poll-settings-button'),
  withInteractionSubmenu = false,
  buttonIcon,
  // eslint-disable-next-line react-hooks/rules-of-hooks
  pollContext: { pollsHistory } = useManager(PollManager, ({ pollsHistory }) => [pollsHistory]),
}: IPollSettingsButtonProps) {
  const { triggerRef, contentRef, isOpen, onOpenPopover, onClosePopover } = useControlledPopover();

  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  const onClickCloseDownloadModal = useCallback(() => setIsDownloadOpen(false), []);

  const onDownloadClick = useCallback(() => {
    setIsDownloadOpen(true);
    onClosePopover();
  }, [onClosePopover]);

  const onClickDownloadModal = useCallback(() => {
    // todo: Track polls, wait Laura.

    setIsDownloadOpen(false);
  }, []);

  const onFocus = useCallback((event: FocusEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  return (
    <LiveErrorBoundary component={'PollSettingsButton'} isDetailed={false}>
      <Menu
        isOpen={isOpen}
        placement={'bottom'}
        closeOnSelect={false}
        onOpen={onOpenPopover}
        onClose={onClosePopover}
      >
        <BokehTooltip label={isOpen ? null : T_SETTINGS} placement={'bottom'}>
          <MenuButton
            as={IconButton}
            ref={triggerRef}
            id={id}
            className={className}
            aria-label={'settings'}
            icon={buttonIcon}
            size={'sm'}
            variant={'minimal'}
            onFocus={onFocus}
          />
        </BokehTooltip>

        <MenuList
          ref={contentRef as LegacyRef<HTMLDivElement>}
          overflow={'unset'}
          width={rem(240)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: rem(4),
          }}
        >
          <MenuItem onClick={onDownloadClick}>{T_DOWNLOAD}</MenuItem>

          {withInteractionSubmenu ? (
            <InteractionToolsSubmenu placement={'right-start'} onCloseParentMenu={onClosePopover} />
          ) : null}
        </MenuList>
      </Menu>

      <DownloadModal
        header={T_DOWNLOAD}
        prefix={'export_poll'}
        history={pollsHistory}
        isOpen={isDownloadOpen}
        onClose={onClickCloseDownloadModal}
        onDownload={onClickDownloadModal}
      />
    </LiveErrorBoundary>
  );
}
